import * as z from 'zod';

import { BorrowerId, EntityId, EventId, LoanId } from '../BrandedIds';
import { zodJSPlainDate } from '../dates/BrandedDate';
import { PaymentDue } from '../PaymentDue';
import { zodAppendOnlyArray } from '../utils/AppendOnlyArray';
import { zodBrandedUuid } from '../utils/Zod';
import { NonNegativeFloat } from '../validations/fields';
import { Appraisal } from './Appraisal';
import { AutopaySettings } from './AutoPaySettings';
import { Bankruptcy } from './Bankruptcy';
import { BillSetting } from './BillSettings';
import { BorrowerInsuranceExpirationInformation } from './BorrowerInsuranceExpirationInformation';
import { Charge } from './Charges';
import { DelinquentInterestSetting } from './DelinquentInterestSetting';
import { Disbursement } from './Disbursement';
import { LoanDocument } from './Document';
import { Draw } from './Draw';
import { EscrowCompany } from './EscrowCompany';
import { EscrowDue } from './EscrowDue';
import { EscrowPaymentHistoryItem } from './EscrowPaymentHistoryItem';
import { EscrowSetting } from './EscrowSetting';
import { Foreclosure } from './Foreclosure';
import { InitialBalances } from './InitialBalances';
import { Inspection } from './Inspection';
import { LoanInvestorRemittanceSplit } from './InvestorRemittanceSplit';
import { LiquidationRecord } from './LiquidationRecord';
import { AmortizationType, ConstructionToPermanentStatus, SpecialLoanStatus } from './Loan';
import { LoanStatus } from './LoanStatus';
import { LoanTransferData } from './LoanTransfer';
import { MaturityConfig } from './MaturityConfig';
import { Modification } from './Modification';
import { NonPerformanceData } from './NonPerformance';
import { NonPerformanceCodeForReportingCycle } from './NonPerformanceCodeForReportingCycle';
import { OriginationBalances } from './OriginationBalances';
import { OriginationData } from './OriginationData';
import { PaymentBreakdown } from './PaymentBreakdown';
import { PaymentConfig } from './PaymentConfig';
import { Payment } from './Payments';
import { PaidOffRecord, Payoff } from './Payoffs';
import { Property } from './Property';
import { Qrpc } from './Qrpc';
import { ReoRecord } from './ReoRecord';
import { ReportableEvent } from './ReportableEvent';
import { BalanceSnapshot, PaymentSnapshot } from './Snapshots';
import { StatementConfig } from './StatementConfig';
import { StopSettings } from './StopSettings';
import { WorkoutPlan } from './WorkoutPlan';
import { Writeoff } from './Writeoff';

export const LoanPaymentStatus = z.enum(['CURRENT', 'PASTDUE']);
export type LoanPaymentStatus = z.infer<typeof LoanPaymentStatus>;

export const LoanSnapshotBorrowerContacts = z.object({
  primaryBorrowerId: zodBrandedUuid<BorrowerId>(),
  additionalBorrowerIds: z.array(zodBrandedUuid<BorrowerId>()),
});
export type LoanSnapshotBorrowerContacts = z.infer<typeof LoanSnapshotBorrowerContacts>;

export const ConstructionConfig = z.object({
  constructionToPermanentStatus: ConstructionToPermanentStatus,
  constructionModificationEffectiveDate: zodJSPlainDate.optional(),
  postModificationAmortizationType: AmortizationType.optional(),
});

export type ConstructionConfig = z.infer<typeof ConstructionConfig>;

export const LoanSnapshotConfig = z.object({
  amortizationMonth: z.number(),
  nextStatementDate: zodJSPlainDate,
  productDescription: z.string(),
  contacts: z.object({
    borrowers: LoanSnapshotBorrowerContacts,
    entity: zodBrandedUuid<EntityId>().optional(),
  }),
  escrowCompanies: z.array(EscrowCompany),
  properties: z.array(Property),
  transferData: LoanTransferData.optional(),
  documents: z.array(LoanDocument),
  principalPurchased: NonNegativeFloat.optional(),

  // balances
  initialBalances: InitialBalances,
  // used for loans boarding as pendingActivation
  originationBalances: OriginationBalances.optional(),
  balanceHistory: z.array(BalanceSnapshot),

  // status and flags
  loanStatus: LoanStatus,
  flagged: z.boolean(),
  nonPerformanceData: NonPerformanceData.optional(),
  foreclosures: z.array(Foreclosure).optional(), // TODO required after migration
  bankruptcies: Bankruptcy.array().optional(),
  reoRecord: ReoRecord.optional(),
  liquidationRecord: LiquidationRecord.optional(),

  /**
   * @deprecated
   * use function getStopSettings instead when validating loan actions
   */
  stopSettings: StopSettings,

  // payments
  billSettings: z.array(BillSetting),
  snapshot: PaymentSnapshot,
  paystring: z.string().optional(),
  paymentsDue: z.array(PaymentDue),
  paymentsMade: z.array(Payment),
  paymentToPaymentDue: zodAppendOnlyArray(PaymentBreakdown),
  fees: z.array(Charge), // TODO migrate to charges
  draws: z.array(Draw),
  disbursements: z.array(Disbursement),
  paymentConfigHistory: z.array(PaymentConfig),
  investorRemittanceSplit: z.array(LoanInvestorRemittanceSplit),
  delinquentInterestSettings: z.array(DelinquentInterestSetting),

  autopaySettings: AutopaySettings.optional(),

  maturityConfig: MaturityConfig,

  constructionConfig: ConstructionConfig.optional(),
  modifications: z.array(Modification),

  originationData: OriginationData.optional(),

  /**
   * date in which LTV is expected to hit 78%, making MI is eligible for cancellation
   * @chelseawillow is checking the exact percentages
   */
  mipLTVThresholdDate: zodJSPlainDate.optional(),
  specialLoanStatus: SpecialLoanStatus.optional(),

  // escrow
  escrowSettings: z.array(EscrowSetting),
  escrowDues: z.array(EscrowDue),
  escrowPaymentHistory: z.array(EscrowPaymentHistoryItem),

  // appraisals & inspections
  appraisals: z.array(Appraisal),
  inspections: z.array(Inspection),

  qrpc: z.array(Qrpc).optional(), // TODO required after migration

  // payoffs
  payoffs: z.array(Payoff),
  paidOffRecord: PaidOffRecord.optional(),
  writeoffs: z.array(Writeoff).optional(), // TODO remove optional when migration has run

  // email tracking
  sentEmails: z.object({
    welcome: z.boolean(),
    gracePastDue: z.boolean(),
    escrowCompany: z.boolean(),
    finalPayment: z.boolean(),
  }),
  enqueuedEmails: z
    .object({
      borrowerInsuranceExpiringSoon: z.array(BorrowerInsuranceExpirationInformation).default([]),
      borrowerInsuranceExpired: z.array(BorrowerInsuranceExpirationInformation).default([]),
    })
    .default({
      borrowerInsuranceExpiringSoon: [],
      borrowerInsuranceExpired: [],
    }),

  workoutPlans: z.array(WorkoutPlan),

  // report generation settings
  reportSettings: z.object({
    tax1098: z.boolean().default(true),
    creditReport: z.boolean().default(true),
  }),

  nextServicerCollectsLatePayments: z.boolean().optional(),

  statementConfig: StatementConfig.optional(), // TODO make required

  nonPerformanceCodeForReportingCycle: z.array(NonPerformanceCodeForReportingCycle).optional(), // LS TODO require after migration
  reportableEvents: z.array(ReportableEvent).optional(), // LS TODO required after migration
});
export type LoanSnapshotConfig = z.infer<typeof LoanSnapshotConfig>;

export interface LoanSnapshotInput {
  loanId: LoanId;
  version: number;
  configuration: LoanSnapshotConfig;
}

export interface LoanSnapshot extends LoanSnapshotInput {
  eventId: EventId;
  createdAt: Date;
  updatedAt: Date;
  changeId: number;
}
